import {
  ContainerOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Dropdown,
  Image,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  TableProps,
  Tooltip,
  Upload,
  message,
} from "antd";
import { useForm } from "antd/lib/form/Form";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { AdmissionApi, DocumentApi, EmployeeApi } from "src/api";
import { Card, Form, FormItem, InputGroup, Option, Select } from "src/components";
import GoodErrorMessage from "src/components/Modal/GoodErrorMessage";
import GoodInfoMessage from "src/components/Modal/GoodInfoMessage";
import { REACT_APP_BASE_PATH } from "src/constants";
import { useChat } from "src/contexts/chat.context";
import { ChatOption } from "src/modules/ai-agent/enums";
import MaskedInput from "src/modules/CustomMaskedInput";
import { ExpiredSessionError, dictionary, i18n, numericRegExp } from "src/utils";

export interface DocumentMenuDropdownProp {
  code: string;
  employeeId: number;
  url: string;
}

type Props = {
  genders: Gender[];
  countryStates: CountryState[];
  onSubmit(values: any): void;
  onCancel(): void;
  employee: Employee | undefined;
  dependents: Dependent[];
  insertDependent(dependent: Dependent): void;
  updateDependent(dependent: Dependent): void;
  loading: boolean;
};

function TabDocuments({
  countryStates,
  onSubmit,
  onCancel,
  employee,
  genders,
  dependents,
  insertDependent,
  updateDependent,
  loading,
}: Props): JSX.Element {
  const { setOption } = useChat();
  const [form] = useForm();

  const getDocument = (documentCode: string) =>
    `${REACT_APP_BASE_PATH}/download/${
      employee?.documentAttachments.find((attach) => attach.document?.code == documentCode)?.attachment ?? ""
    }`;

  const columns: TableProps<DocumentAttachment>["columns"] = [
    {
      title: i18n(dictionary.label.name),
      dataIndex: "id",
      key: "id",
      render: (id, record) => {
        return record.fileName ?? record.document.description;
      },
    },
    {
      title: i18n(dictionary.label.actions),
      dataIndex: "attachment",
      key: "attachment",
      render: (attachment, record) => {
        return (
          <Space>
            {/* <Tooltip title={i18n(dictionary.action.view)}>
              <Button
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => {
                  Modal.info({
                    title: i18n(dictionary.label.view),
                    content: (
                      <Image
                        src={`${REACT_APP_BASE_PATH}/download/${attachment}`}
                        fallback={
                          "data:image/png;base64,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"
                        }
                        placeholder={<Spin />}
                      />
                    ),
                  });
                }}
              />
            </Tooltip> */}
            <Tooltip title={i18n(dictionary.action.download)}>
              <Button
                shape="circle"
                icon={<DownloadOutlined />}
                onClick={() => {
                  downloadDocument(`${REACT_APP_BASE_PATH}/download/${attachment}`, record.fileId, record.fileName);
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // #region DOCUMENTS UPLOAD
  const [urlUploadedFileIdentity, setUrlUploadedFileIdentity] = useState<string>(getDocument("RG"));
  const [urlUploadedFileCpf, setUrlUploadedFileCpf] = useState<string>(getDocument("CPF"));
  const [urlUploadedFilePis, setUrlUploadedFilePis] = useState<string>(getDocument("PIS"));
  const [urlUploadedFileCt, setUrlUploadedFileCt] = useState<string>(getDocument("CT"));
  const [urlUploadedFileTe, setUrlUploadedFileTe] = useState<string>(getDocument("TE"));
  const [urlUploadedFileCnh, setUrlUploadedFileCnh] = useState<string>(getDocument("CNH"));
  const [urlUploadedFileNr, setUrlUploadedFileNr] = useState<string>(getDocument("NR"));
  const [urlUploadedFileCMEST, setUrlUploadedFileCMEST] = useState<string>(getDocument("CMEST"));
  const [urlUploadedFileTce, setUrlUploadedFileTce] = useState<string>(getDocument("TCE"));
  const [urlUploadedFileCe, setUrlUploadedFileCe] = useState<string>(getDocument("CE"));

  // #endregion
  const [hasForeignAddress, setHasForeignAddress] = useState<boolean>(false);

  const [cpf, setCpf] = useState<string>(employee?.cpf ?? "");
  const [zipCode, setZipCode] = useState<string>(employee?.address?.zipCode ?? "");

  function DocumentMenuDropdown(props: DocumentMenuDropdownProp): JSX.Element {
    const [file, setFile] = useState("");

    function getBase64(img: any, callback: (result: any) => void) {
      const reader = new FileReader();
      reader.addEventListener("load", () => callback(reader.result));
      reader.readAsDataURL(img);
    }

    const bytesToMb = (bytes: number) => bytes / 1000000;

    const fileIsTooLarge = (file: any) => bytesToMb(file.size) > 1;

    const fileIsImage = (file: any) => file.type.startsWith("image/");

    // Atualiza a url do documento de acordo com o seu respectivo código
    function updateUrlUpload(documentCode: string, url: string) {
      const urlBasePath = `${REACT_APP_BASE_PATH}/download/`;

      const urlSetters: Record<string, React.Dispatch<React.SetStateAction<string>>> = {
        RG: setUrlUploadedFileIdentity,
        CPF: setUrlUploadedFileCpf,
        PIS: setUrlUploadedFilePis,
        CT: setUrlUploadedFileCt,
        TE: setUrlUploadedFileTe,
        CNH: setUrlUploadedFileCnh,
        NR: setUrlUploadedFileNr,
        CMEST: setUrlUploadedFileCMEST,
        TCE: setUrlUploadedFileTce,
        CE: setUrlUploadedFileCe,
      };

      const setter = urlSetters[documentCode];
      if (setter) setter(urlBasePath + url);
    }

    async function handleUpload(info: any, documentCode: string) {
      if (info.file.percent == 100) {
        if (!fileIsImage(info.file)) {
          Modal.warning({
            title: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_IMAGE.title),
            content: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_IMAGE.message),
          });
          return;
        }

        if (fileIsTooLarge(info.file)) {
          Modal.warning({
            title: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_SIZE.title),
            content: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_SIZE.message),
          });
          return;
        }

        getBase64(info.file.originFileObj, async (fileURL) => {
          const fileBuffer = fileURL.replace(/(data:image\/(jpeg)*(gif)*(png)*\;(base64),)/g, "");
          setFile(fileBuffer);
          try {
            const res = await DocumentApi.uploadFile(info.file.originFileObj, documentCode, props.employeeId);
            updateUrlUpload(documentCode, res.url);
            GoodInfoMessage({
              title: i18n(dictionary.success.ATTACH_DOCUMENT_SUCCESS.title),
              message: i18n(dictionary.success.ATTACH_DOCUMENT_SUCCESS.message),
            });
          } catch (error) {
            if (!(error instanceof ExpiredSessionError)) {
              GoodErrorMessage({
                title: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR.title),
                message: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR.message),
                onClickLink: () => setOption(ChatOption.CHAT),
                onOk: () => handleUpload(info, documentCode),
              });
            }
            console.error(error);
          }
        });
      }
    }

    const documentMenuItems = [
      {
        icon: <EyeOutlined />,
        label: i18n(dictionary.action.view),
        key: "1",
        onClick: () => {
          Modal.info({
            title: "Visualização",
            content: (
              <Image
                src={props.url}
                fallback={
                  "data:image/png;base64,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"
                }
                placeholder={<Spin />}
              />
            ),
          });
        },
      },
      {
        label: (
          <FormItem
            name="identityAttachment"
            noStyle
          >
            <Upload
              accept={"image/*"}
              showUploadList={false}
              multiple={false}
              onChange={async (info) => await handleUpload(info, props.code)}
            >
              <UploadOutlined /> {i18n(dictionary.action.add_document)}
            </Upload>
          </FormItem>
        ),
        key: "2",
      },
    ];

    return (
      <Dropdown
        menu={{ items: documentMenuItems }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <EllipsisOutlined />
      </Dropdown>
    );
  }

  const [downloadingContract, setDownloadingContract] = useState(false);

  async function downloadContract(employeeId: number | undefined) {
    message.destroy();
    setDownloadingContract(true);
    if (employeeId) {
      const response = await AdmissionApi.downloadContract(employeeId).catch((_) => false);
      if (!response) {
        message.error(i18n(dictionary.label.error_download_contract));
      }
    }
    setDownloadingContract(false);
  }

  async function downloadDocument(attachment?: string, fileId?: string, fileName?: string) {
    message.destroy();
    setDownloadingContract(true);
    if (fileId) {
      const blob = await EmployeeApi.downloadDocument(fileId);
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName + ".pdf";
      link.click();
      window.URL.revokeObjectURL(url);
    } else {
      if (attachment) {
        const link = document.createElement("a");
        link.href = attachment;
        link.download = fileName + ".pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }

    setDownloadingContract(false);
  }

  function loadForm() {
    form.setFieldsValue({
      ...employee,
      ...employee?.additionalData,
      ...employee?.eSocialData,
      ...employee?.eSocialData?.employeeForeignAddress,
      ...employee?.address,
      driverLicenseExpirationDate: employee?.additionalData?.driverLicenseExpirationDate
        ? dayjs(employee?.additionalData?.driverLicenseExpirationDate)
        : undefined,
      foreignAddress: employee?.eSocialData?.employeeForeignAddress,
    });
    setCpf(employee?.cpf ?? "");
    setZipCode(employee?.address?.zipCode ?? "");
  }

  useEffect(() => {
    loadForm();

    setHasForeignAddress(employee?.eSocialData?.hasForeignAddress ?? false);
  }, []);

  return (
    <Form
      form={form}
      onSubmit={(values) => onSubmit({ ...values, tab: "3", dependents })}
      onCancel={() => onCancel()}
      submitText={i18n(dictionary.action.update_save)}
      loading={loading}
    >
      <Card
        icon={<ContainerOutlined />}
        title={i18n(dictionary.label.registered_documents)}
      >
        <FormItem
          label={i18n(dictionary.label.rg)}
          name="identity"
          rules={[{ required: false }, { max: 28 }]}
          hasFeedback
          validateFirst
        >
          <Input
            addonAfter={
              <DocumentMenuDropdown
                code="RG"
                employeeId={employee?.id ?? 0}
                url={urlUploadedFileIdentity}
              />
            }
          />
        </FormItem>
        <FormItem
          label={i18n(dictionary.label.cpf)}
          name="cpf"
          rules={[{ required: true }, { max: 14 }]}
          hasFeedback
          validateFirst
        >
          <MaskedInput
            mask={"000.000.000-00"}
            value={cpf}
            defaultValue={cpf}
            addonAfter={
              <DocumentMenuDropdown
                code="CPF"
                employeeId={employee?.id ?? 0}
                url={urlUploadedFileCpf}
              />
            }
          />
        </FormItem>
        <FormItem
          label={i18n(dictionary.label.pis_pasep)}
          name="pisPasep"
          rules={[{ required: false }, { max: 20 }]}
          hasFeedback
          validateFirst
        >
          <Input
            addonAfter={
              <DocumentMenuDropdown
                code="PIS"
                employeeId={employee?.id ?? 0}
                url={urlUploadedFilePis}
              />
            }
          />
        </FormItem>
        <FormItem>
          <InputGroup>
            <FormItem
              label={i18n(dictionary.label.work_card_number)}
              name="workCardNumber"
              rules={[{ required: false }, { max: 20 }]}
              hasFeedback
              validateFirst
            >
              <Input
                addonAfter={
                  <DocumentMenuDropdown
                    code="CT"
                    employeeId={employee?.id ?? 0}
                    url={urlUploadedFileCt}
                  />
                }
              />
            </FormItem>
            <InputGroup>
              <FormItem
                label={i18n(dictionary.label.work_card_serie)}
                name="workCardSerie"
                rules={[{ required: false }, { max: 255 }]}
                hasFeedback
                validateFirst
              >
                <MaskedInput mask={[{ mask: numericRegExp }]} />
              </FormItem>
              <FormItem
                label={i18n(dictionary.label.work_card_state)}
                name="workCardState"
                rules={[{ required: false }]}
                hasFeedback
                validateFirst
              >
                <Select allowClear>
                  {countryStates.map((countryState) => (
                    <Option
                      key={countryState.abbreviation}
                      title={countryState.abbreviation}
                      value={countryState.abbreviation}
                    >
                      {countryState.abbreviation}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </InputGroup>
          </InputGroup>
        </FormItem>
        <FormItem>
          <InputGroup>
            <FormItem
              label={i18n(dictionary.label.voter_registration_number)}
              name="voterRegistrationNumber"
              rules={[{ required: false }, { max: 30 }]}
              hasFeedback
              validateFirst
            >
              <Input
                addonAfter={
                  <DocumentMenuDropdown
                    code="TE"
                    employeeId={employee?.id ?? 0}
                    url={urlUploadedFileTe}
                  />
                }
              />
            </FormItem>
            <InputGroup>
              <FormItem
                label={i18n(dictionary.label.voter_registration_section)}
                name="voterRegistrationSection"
                rules={[{ required: false }, { max: 255 }]}
                hasFeedback
                validateFirst
              >
                <MaskedInput mask={[{ mask: numericRegExp }]} />
              </FormItem>
              <FormItem
                label={i18n(dictionary.label.voter_registration_zone)}
                name="voterRegistrationZone"
                rules={[{ required: false }, { max: 255 }]}
                hasFeedback
                validateFirst
              >
                <MaskedInput mask={[{ mask: numericRegExp }]} />
              </FormItem>
            </InputGroup>
          </InputGroup>
        </FormItem>
        <FormItem>
          <InputGroup>
            <FormItem
              label={i18n(dictionary.label.driver_license)}
              name="driverLicense"
              rules={[{ required: false }, { max: 255 }]}
              hasFeedback
              validateFirst
            >
              <Input
                addonAfter={
                  <DocumentMenuDropdown
                    code="CNH"
                    employeeId={employee?.id ?? 0}
                    url={urlUploadedFileCnh}
                  />
                }
              />
            </FormItem>
            <FormItem
              label={i18n(dictionary.label.driver_license_expiration_date)}
              name="driverLicenseExpirationDate"
              rules={[{ required: false }]}
              hasFeedback
              validateFirst
            >
              <DatePicker format={[i18n(dictionary.dateFormat)]} />
            </FormItem>
          </InputGroup>
        </FormItem>
        <FormItem>
          <InputGroup>
            <FormItem
              label={i18n(dictionary.label.reservist_number)}
              name="reservistNumber"
              rules={[{ required: false }, { max: 28 }]}
              hasFeedback
              validateFirst
            >
              <Input
                addonAfter={
                  <DocumentMenuDropdown
                    code="NR"
                    employeeId={employee?.id ?? 0}
                    url={urlUploadedFileNr}
                  />
                }
              />
            </FormItem>
            <FormItem
              label={i18n(dictionary.label.reservist_serie)}
              name="reservistSerie"
              rules={[{ required: false }, { max: 255 }]}
              hasFeedback
              validateFirst
            >
              <Input />
            </FormItem>
          </InputGroup>
        </FormItem>
        <FormItem>
          <InputGroup>
            <FormItem
              label={i18n(dictionary.label.enrollmentEducation)}
              name="enrollmentEducation"
              rules={[{ required: false }, { max: 255 }]}
              hasFeedback
              validateFirst
            >
              <Input
                addonAfter={
                  <DocumentMenuDropdown
                    code="CMEST"
                    employeeId={employee?.id ?? 0}
                    url={urlUploadedFileCMEST}
                  />
                }
              />
            </FormItem>
            <FormItem
              label={i18n(dictionary.label.internshipCommitmentTerm)}
              name="internshipCommitmentTerm"
              rules={[{ required: false }, { max: 255 }]}
              hasFeedback
              validateFirst
            >
              <Input
                addonAfter={
                  <DocumentMenuDropdown
                    code="TCE"
                    employeeId={employee?.id ?? 0}
                    url={urlUploadedFileTce}
                  />
                }
              />
            </FormItem>
          </InputGroup>
        </FormItem>
        <FormItem label={i18n(dictionary.label.work_contract)}>
          <Button
            disabled={!employee?.additionalData?.contract}
            loading={downloadingContract}
            icon={<DownloadOutlined />}
            onClick={() => downloadContract(employee?.id)}
          >
            {i18n(dictionary.label.download_contract)}
          </Button>
        </FormItem>
      </Card>
      <Card
        title={i18n(dictionary.label.employee_address)}
        icon={<EnvironmentOutlined />}
      >
        <FormItem
          label={i18n(dictionary.label.address_street)}
          name="publicPlace"
          rules={[{ required: false }, { max: 255 }]}
          hasFeedback
          validateFirst
        >
          <Input
            addonAfter={
              <DocumentMenuDropdown
                code="CE"
                employeeId={employee?.id ?? 0}
                url={urlUploadedFileCe}
              />
            }
          />
        </FormItem>
        <FormItem
          label={i18n(dictionary.label.address_zip_code)}
          name="zipCode"
          rules={[{ required: false }, { max: 9 }]}
          hasFeedback
          validateFirst
        >
          <MaskedInput
            mask="00000-000"
            value={zipCode}
            defaultValue={zipCode}
          />
        </FormItem>
        <FormItem>
          <InputGroup>
            <FormItem
              label={i18n(dictionary.label.address_number)}
              name="addressNumber"
              rules={[{ required: false }, { max: 255 }]}
              hasFeedback
              validateFirst
            >
              <Input min={0} />
            </FormItem>
            <FormItem
              label={i18n(dictionary.label.address_complement)}
              name="complement"
              rules={[{ required: false }, { max: 255 }]}
              hasFeedback
              validateFirst
            >
              <Input />
            </FormItem>
          </InputGroup>
        </FormItem>
        <FormItem
          label={i18n(dictionary.label.address_district)}
          name="zone"
          rules={[{ required: false }, { max: 255 }]}
          hasFeedback
          validateFirst
        >
          <Input />
        </FormItem>
        <FormItem
          label={i18n(dictionary.label.address_city)}
          name="city"
          rules={[{ required: false }, { max: 255 }]}
          hasFeedback
          validateFirst
        >
          <Input />
        </FormItem>
        <FormItem
          label={i18n(dictionary.label.address_state)}
          name="state"
          rules={[{ required: false }]}
          hasFeedback
          validateFirst
        >
          <Select allowClear>
            {countryStates.map((countryState) => (
              <Option
                key={countryState.abbreviation}
                title={countryState.name}
                value={countryState.abbreviation}
              >
                {countryState.name}
              </Option>
            ))}
          </Select>
        </FormItem>
        {/* <FormItem
          label={i18n(dictionary.label.has_address_abroad)}
          name="hasForeignAddress"
          rules={[{ required: false }]}
          initialValue={hasForeignAddress}
        >
          <Switch
            checkedChildren={i18n(dictionary.action.yes)}
            unCheckedChildren={i18n(dictionary.action.no)}
            onChange={(checked: boolean) => {
              form.setFieldsValue({ hasForeignAddress: checked });
              setHasForeignAddress(checked);
            }}
            checked={hasForeignAddress}
          />
        </FormItem> */}
      </Card>
      {/* {hasForeignAddress && <ForeignAddressCard hasForeignAddress={hasForeignAddress} />}
      <Dependents
        genders={genders}
        employeeEsocialId={employee?.eSocialData.id}
        dependents={dependents}
        updateDependent={updateDependent}
        insertDependent={insertDependent}
      /> */}
      <Card
        icon={<PaperClipOutlined />}
        title={i18n(dictionary.label.otherFileDocument)}
      >
        <Table<DocumentAttachment>
          columns={columns}
          dataSource={employee?.documentAttachments}
          pagination={{ pageSize: 5, showSizeChanger: false }}
        />
      </Card>
    </Form>
  );
}

export default TabDocuments;
